import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { NativeSyntheticEvent, NativeScrollEvent } from 'react-native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { configureOperations, getMachinesOperations } from '@reducers/machines';
import { useGetMachineById } from '@api/modules/machines/hooks';
import { getStateParams } from '@helpers';
import { scale, width } from '@components/frame';
import { Operation } from '@interfaces/machines';
import { PAGE_SIZE } from '@components/settings/v2/structure/machines/machineDetails/constants';

export interface SelectOperation {
  operation: Operation;
  selected: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useManageMachineAssignedOperations = () => {
  const route = useRoute();
  const dispatch = useAppDispatch();

  const id = Number(getStateParams(route).id);

  const { machine } = useGetMachineById({ id });
  const [operations, setOperations] = useState<Operation[]>([]);
  const [allOperations, setAllOperations] = useState<Operation[]>([]);
  const [operationsLoaded, setOperationsLoaded] = useState(false);
  const loaded = machine != null && operationsLoaded;

  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const [containerHeight, setContainerHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      const ops = await dispatch(getMachinesOperations());
      const machineOps = ops?.filter((op: Operation) => op.machines?.some((x: any) => x.machineId === id));
      setAllOperations(ops);
      setOperations(machineOps);
      setOperationsLoaded(true);
    })();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [allOperations]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const elementWidth = (width - scale(366) - scale(18) * 2 - scale(4) * 2) / 3;

  const selectedList = useMemo(
    () => operations?.map((i) => i.operationId) ?? [],
    [operations]
  );

  const visibleOperations = useMemo(
    () => allOperations.slice(0, page * PAGE_SIZE),
    [allOperations, page]
  );
  const hasMore = visibleOperations.length < allOperations.length;

  const maybeLoadMore = useCallback(() => {
    if (hasMore && contentHeight < containerHeight) {
      setPage((p) => p + 1);
    }
  }, [hasMore, contentHeight, containerHeight]);

  useEffect(() => {
    maybeLoadMore();
  }, [contentHeight, containerHeight, maybeLoadMore]);

  const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
    if (layoutMeasurement.height + contentOffset.y >= contentSize.height - 40 && hasMore && !loadingMore) {
      setLoadingMore(true);
      scrollTimeoutRef.current = setTimeout(() => {
        setPage((p) => p + 1);
        setLoadingMore(false);
      }, 350);
    }
  }, [hasMore, loadingMore]);

  const selectOperation = async ({ operation, selected, setSaving }: SelectOperation) => {
    if (!machine) {
      return;
    }
    setSaving(true);
    const result = await dispatch(
      configureOperations({
        operationId: operation.operationId,
        machines: [
          {
            machineId: machine.machineId,
            action: selected ? 'remove' : 'add',
          },
        ],
      })
    );
    if (result?.successDetails) {
      const data = await dispatch(getMachinesOperations());
      const currentOperations = data?.filter((op: Operation) => op.machines?.some((m: any) => m.machineId === id)) ?? [];
      setOperations(currentOperations);
    }
    setSaving(false);
  };

  const onContainerLayout = useCallback((e: any) => setContainerHeight(e.nativeEvent.layout.height), []);
  const onContentSizeChange = useCallback((_: number, h: number) => setContentHeight(h), []);

  return {
    id,
    machine,
    loaded,
    visibleOperations,
    selectedList,
    hasMore,
    loadingMore,
    elementWidth,
    handleScroll,
    selectOperation,
    onContainerLayout,
    onContentSizeChange,
  };
};
